import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Check,
  Copy,
  Facebook,
  Gift,
  Loader2,
  MessageCircle,
  Send,
  Share2,
  Sparkles,
  UserPlus,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * ReferralMilestoneProgress — 5-pip progress toward the next milestone.
 *
 * Milestones are multiples of `milestone_size` (default 5). At any time,
 * the user is progressing from the last-reached milestone toward the next.
 * The 5 pips fill green as verified invites accumulate; a subtle caption
 * shows the fraction (e.g. "3 / 5 verified · next reward at 5").
 */
function ReferralMilestoneProgress({ stats }) {
  const size = stats.milestone_size || 5;
  const verified = stats.verified_count || 0;
  const lastMilestone = Math.floor(verified / size) * size;
  const nextMilestone = lastMilestone + size;
  const inCurrent = verified - lastMilestone; // 0..size

  return (
    <div className="col-span-2 md:col-span-1">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
        Next reward
      </div>
      <div
        className="mt-2 flex items-center gap-1.5"
        data-testid="referral-milestone-pips"
        aria-label={`${inCurrent} of ${size} verified invites toward next reward`}
      >
        {Array.from({ length: size }, (_, i) => {
          const filled = i < inCurrent;
          return (
            <span
              key={i}
              className={
                "h-2 flex-1 rounded-full transition-colors " +
                (filled ? "bg-brand-green" : "bg-gray-200")
              }
              data-testid={`referral-milestone-pip-${i}${filled ? "-filled" : ""}`}
            />
          );
        })}
      </div>
      <div
        className="mt-2 text-[11px] font-medium text-gray-500"
        data-testid="referral-milestone-caption"
      >
        <span className="tabular-nums text-gray-800">{inCurrent}</span>
        <span className="text-gray-500"> / {size} verified </span>
        <span className="text-gray-400">
          · next reward at {nextMilestone}
        </span>
      </div>
    </div>
  );
}

/**
 * ReferralTile — dashboard card that shows the broker's referral code + link,
 * copy affordance, invite/verified counts, and a "Preview & share" modal
 * with the dynamic OG card + WhatsApp / Viber / Facebook one-tap shares.
 */
export function ReferralTile() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/referral");
      setStats(data);
    } catch (e) {
      toast.error(formatApiError(e, "Failed to load referral info"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cardImageUrl = useMemo(() => {
    if (!stats?.code) return null;
    // Same origin as the API — derive from invite_link.
    try {
      const u = new URL(stats.invite_link);
      return `${u.origin}/api/referral/card/${stats.code}.png`;
    } catch {
      return `/api/referral/card/${stats.code}.png`;
    }
  }, [stats]);

  // Copy-link UTM: applied at copy time only (the displayed URL stays clean).
  // Matches the modal Copy button so both channels attribute to `copy_link`.
  const copyLinkUrl = useMemo(() => {
    if (!stats?.invite_link) return "";
    try {
      const u = new URL(stats.invite_link);
      u.searchParams.set("utm_source", "copy_link");
      u.searchParams.set("utm_medium", "referral");
      u.searchParams.set("utm_campaign", "broker_invite");
      return u.toString();
    } catch {
      const sep = stats.invite_link.includes("?") ? "&" : "?";
      return `${stats.invite_link}${sep}utm_source=copy_link&utm_medium=referral&utm_campaign=broker_invite`;
    }
  }, [stats]);

  const copyToClipboard = async (label, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(label);
      toast.success(`${label} copied — opening share options`);
      setTimeout(() => setCopiedField(null), 2200);
      if (label === "Link") setShareOpen(true);
    } catch {
      toast.error("Clipboard blocked — you can still tap a share option below.");
      if (label === "Link") setShareOpen(true);
    }
  };

  return (
    <section
      className="relative overflow-hidden rounded-2xl border border-brand-green/20 bg-white p-8 shadow-mb-card"
      data-testid="referral-tile"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand-green/8 blur-3xl"
      />
      <div className="relative flex items-start justify-between gap-6">
        <div>
          <div className="inline-flex items-center gap-1.5 rounded-full border border-brand-green/25 bg-brand-green/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-brand-green">
            <Gift className="h-3 w-3" />
            Refer a colleague
          </div>
          <h3
            className="mt-3 text-2xl font-bold tracking-tight text-gray-900"
            data-testid="referral-tile-headline"
          >
            Earn 30 days of MatchBroker Pro for every 5 verified invites.
          </h3>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-gray-500">
            Send your unique link — WhatsApp, Viber, Facebook — to a fellow
            PRC-licensed broker. Every 5 who get verified unlocks a fresh
            30-day Pro block for you.
          </p>
        </div>
        <span className="hidden flex-none rounded-full bg-brand-navy/5 p-3 text-brand-navy sm:inline-flex">
          <Sparkles className="h-6 w-6" />
        </span>
      </div>

      {loading ? (
        <div className="relative mt-8 flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading your referral details…
        </div>
      ) : stats ? (
        <div className="relative mt-8 grid grid-cols-1 gap-4 md:grid-cols-[1fr,1fr] md:gap-6">
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              Your referral code
            </div>
            <div className="mt-2 flex items-center justify-between gap-3">
              <div
                className="select-all font-mono text-2xl font-bold tracking-tight text-brand-navy"
                data-testid="referral-code-value"
              >
                {stats.code}
              </div>
              <button
                type="button"
                onClick={() => copyToClipboard("Code", stats.code)}
                className="mb-focus-ring inline-flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                data-testid="referral-copy-code"
              >
                {copiedField === "Code" ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-brand-green" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>
          <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              Invite link
            </div>
            <div className="mt-2 flex items-center gap-2">
              <div
                className="min-w-0 flex-1 truncate rounded-md border border-gray-200 bg-white px-2 py-1.5 font-mono text-xs text-gray-700"
                data-testid="referral-invite-link-value"
                title={stats.invite_link}
              >
                {stats.invite_link}
              </div>
              <button
                type="button"
                onClick={() => copyToClipboard("Link", copyLinkUrl)}
                className="mb-focus-ring inline-flex h-9 flex-none items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-100"
                data-testid="referral-copy-link"
              >
                {copiedField === "Link" ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-brand-green" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" />
                    Copy
                  </>
                )}
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="mb-focus-ring mt-3 inline-flex h-9 items-center gap-1.5 rounded-lg bg-brand-navy px-3 text-xs font-semibold text-white hover:bg-brand-navy-hover"
              data-testid="referral-open-share"
            >
              <Share2 className="h-3.5 w-3.5" />
              Preview &amp; share
            </button>
          </div>

          <div
            className="col-span-full grid grid-cols-2 gap-4 border-t border-gray-100 pt-6 md:grid-cols-3"
            data-testid="referral-stats"
          >
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                Invited
              </div>
              <div
                className="mt-1 text-2xl font-bold text-gray-900"
                data-testid="referral-invited-count"
              >
                {stats.invited_count}
              </div>
            </div>
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                Verified
              </div>
              <div
                className="mt-1 flex items-baseline gap-2 text-2xl font-bold text-brand-green"
                data-testid="referral-verified-count"
              >
                {stats.verified_count}
                <UserPlus className="h-4 w-4 text-brand-green" />
              </div>
            </div>
            <ReferralMilestoneProgress stats={stats} />
          </div>
        </div>
      ) : null}

      <ReferralShareModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        stats={stats}
        cardImageUrl={cardImageUrl}
      />
    </section>
  );
}

function ReferralShareModal({ open, onOpenChange, stats, cardImageUrl }) {
  if (!stats) return null;
  const shareUrl = stats.invite_link;
  const message = `Join my verified broker network on MatchBroker and get 30 days free 👉`;

  // Append channel UTM params to any share URL so the /register hop can
  // capture the source. Backend sanitizes and stores as `signup_source`.
  const utmize = (source) => {
    try {
      const u = new URL(shareUrl);
      u.searchParams.set("utm_source", source);
      u.searchParams.set("utm_medium", "referral");
      u.searchParams.set("utm_campaign", "broker_invite");
      return u.toString();
    } catch {
      const sep = shareUrl.includes("?") ? "&" : "?";
      return `${shareUrl}${sep}utm_source=${source}&utm_medium=referral&utm_campaign=broker_invite`;
    }
  };

  const channels = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      href: `https://wa.me/?text=${encodeURIComponent(`${message} ${utmize("whatsapp")}`)}`,
      icon: MessageCircle,
      tone: "bg-[#25D366] text-white hover:bg-[#1FB255]",
    },
    {
      key: "viber",
      label: "Viber",
      href: `viber://forward?text=${encodeURIComponent(`${message} ${utmize("viber")}`)}`,
      icon: Send,
      tone: "bg-[#7360F2] text-white hover:bg-[#5C4CD1]",
    },
    {
      key: "facebook",
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(utmize("facebook"))}`,
      icon: Facebook,
      tone: "bg-[#1877F2] text-white hover:bg-[#166FE5]",
    },
  ];

  const copyUrl = utmize("copy_link");

  // Per-channel attribution breakdown from the referral stats.
  // Only render channels with at least one invited broker.
  const CHANNEL_LABELS = {
    whatsapp: "WhatsApp",
    viber: "Viber",
    facebook: "Facebook",
    copy_link: "Copy link",
    direct: "Direct",
  };
  const breakdownEntries = Object.entries(stats.channel_breakdown || {})
    .filter(([, v]) => v && (v.invited || 0) > 0)
    .sort(([, a], [, b]) => (b.invited || 0) - (a.invited || 0));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-2rem)] max-w-lg overflow-hidden rounded-2xl border-gray-200 bg-white p-0 sm:p-0"
        data-testid="referral-share-modal"
      >
        <div className="min-w-0 border-b border-gray-100 px-6 pt-6 sm:px-8 sm:pt-8">
          <DialogTitle
            className="text-2xl font-bold tracking-tight text-gray-900"
            data-testid="referral-share-title"
          >
            Share your invite
          </DialogTitle>
          <DialogDescription className="mt-1 text-sm text-gray-500">
            A live preview of what your invitees will see — tap a channel to
            share.
          </DialogDescription>
        </div>

        <div className="min-w-0 px-6 py-6 sm:px-8">
          <div
            className="overflow-hidden rounded-xl border border-gray-100 bg-gray-50"
            data-testid="referral-card-preview-wrapper"
          >
            {cardImageUrl ? (
              <img
                src={cardImageUrl}
                alt="Referral card preview"
                loading="lazy"
                width="1200"
                height="630"
                className="block h-auto w-full max-w-full object-contain"
                data-testid="referral-card-preview"
              />
            ) : (
              <div className="p-12 text-center text-sm text-gray-400">
                Preview unavailable.
              </div>
            )}
          </div>

          <div className="mt-6 min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              Share via
            </div>
            <div
              className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3"
              data-testid="referral-share-channels"
            >
              {channels.map((c) => (
                <a
                  key={c.key}
                  href={c.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mb-focus-ring inline-flex h-11 items-center justify-center gap-2 rounded-xl text-xs font-semibold transition ${c.tone}`}
                  data-testid={`referral-share-${c.key}`}
                >
                  <c.icon className="h-4 w-4" />
                  {c.label}
                </a>
              ))}
            </div>
          </div>

          <div className="mt-6 flex min-w-0 items-center gap-2">
            <div
              className="min-w-0 flex-1 truncate rounded-md border border-gray-200 bg-white px-3 py-2 font-mono text-xs text-gray-700"
              title={copyUrl}
              data-testid="referral-share-url"
            >
              {copyUrl}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(copyUrl);
                  toast.success("Link copied");
                } catch {
                  toast.error("Clipboard blocked");
                }
              }}
              className="mb-focus-ring h-9 rounded-lg border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-100"
              data-testid="referral-share-copy"
            >
              <Copy className="mr-1.5 h-3.5 w-3.5" />
              Copy
            </Button>
          </div>

          {breakdownEntries.length > 0 && (
            <div
              className="mt-6 min-w-0 border-t border-gray-100 pt-4"
              data-testid="referral-channel-breakdown"
            >
              <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                Where your invites come from
              </div>
              <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-2 text-xs text-gray-600">
                {breakdownEntries.map(([key, v]) => (
                  <li
                    key={key}
                    className="inline-flex items-center gap-1.5"
                    data-testid={`referral-channel-${key}`}
                  >
                    <span className="font-medium text-gray-700">
                      {CHANNEL_LABELS[key] || key}
                    </span>
                    <span
                      className="tabular-nums text-gray-500"
                      data-testid={`referral-channel-${key}-count`}
                    >
                      {v.invited} invited · {v.verified} verified
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
